import React, { useState } from 'react';
import { useInventoryHealth } from '../../../../hooks/useInventoryHealth';
import { HealthMetricCards } from './HealthMetricCards';
import { HealthRuleFilterBar } from './HealthRuleFilterBar';
import { HealthPropertyTableRow } from './HealthPropertyTableRow';
import { HealthQuickEditModal } from './HealthQuickEditModal';
import { PropertyHealthAudit } from '../../../../types/inventoryHealth';
import { SpinnerIcon, ExclamationTriangleIcon } from '../../../../components/Icons';

export const AdminInventoryHealthSection: React.FC = () => {
  const {
    audits,
    stats,
    owners,
    loading,
    error,
    search,
    setSearch,
    selectedRule,
    setSelectedRule,
    selectedOwner,
    setSelectedOwner,
    refresh,
    quickSave,
    exportCsv,
  } = useInventoryHealth();

  const [editingAudit, setEditingAudit] = useState<PropertyHealthAudit | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const totalPages = Math.ceil(audits.length / PAGE_SIZE) || 1;
  const paginatedAudits = audits.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading && audits.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 animate-pulse">
        <SpinnerIcon className="w-10 h-10 text-sky-600 mb-3" />
        <p className="text-xs font-black uppercase tracking-widest text-gray-400">
          Auditando salud del inventario 183150...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-gray-100 pb-4">
        <div>
          <span className="text-xs font-black uppercase tracking-wider text-sky-600">Módulo de Calidad Operativa</span>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Salud del Inventario Activo (183150)</h2>
        </div>
        <p className="text-xs text-gray-500 max-w-md">
          Monitoreo en tiempo real de inconsistencias: administración, piso, ascensor, ubicación geográfica, áreas, precios y dueños.
        </p>
      </div>

      <HealthMetricCards stats={stats} />

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 flex items-center gap-3 text-xs font-bold">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
        <HealthRuleFilterBar
          selectedRule={selectedRule}
          onSelectRule={(r) => { setSelectedRule(r); setPage(1); }}
          search={search}
          onSearchChange={(s) => { setSearch(s); setPage(1); }}
          stats={stats}
          owners={owners}
          selectedOwner={selectedOwner}
          onSelectOwner={(o) => { setSelectedOwner(o); setPage(1); }}
          onRefresh={refresh}
          onExportCsv={exportCsv}
        />

        <div className="overflow-x-auto custom-scrollbar border border-gray-100 rounded-2xl">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Inmueble / Ciudad / Dueño</th>
                <th className="px-4 py-3">Datos Clave</th>
                <th className="px-4 py-3">Salud</th>
                <th className="px-4 py-3">Observaciones Detectadas</th>
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedAudits.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400 text-xs font-bold">
                    No se encontraron inmuebles con los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                paginatedAudits.map((a) => (
                  <HealthPropertyTableRow
                    key={a.property.id}
                    audit={a}
                    onEdit={setEditingAudit}
                    onSelectRuleFilter={setSelectedRule}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs font-bold text-gray-500 px-2">
            <span>Mostrando {paginatedAudits.length} de {audits.length} inmuebles</span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 transition-colors"
              >
                Anterior
              </button>
              <span>Página {page} de {totalPages}</span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 transition-colors"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      <HealthQuickEditModal
        audit={editingAudit}
        isOpen={!!editingAudit}
        onClose={() => setEditingAudit(null)}
        onSave={quickSave}
      />
    </div>
  );
};
